import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import adminApi from "../../../services/adminApi";
import ReportShell from "../ReportShell";
import {
  DateRange,
  DEFAULT_RANGE,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
  RANGE_PRESETS,
  rangeLabel,
  rangeParams,
} from "../reportCommon";
import {
  ExportColumn,
  exportCsv,
  exportPdf,
  formatCurrency,
  pdfCurrency,
  PharmacyHeader,
} from "../../../utils/reportExport";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  paid: "green",
  pending: "orange",
  partially_paid: "gold",
  refunded: "red",
};

interface Props {
  pharmacy: PharmacyHeader;
}

const CollectionReport: React.FC<Props> = ({ pharmacy }) => {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [paymentMethod, setPaymentMethod] = useState<string | undefined>();
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
  const [createdBy, setCreatedBy] = useState<string | undefined>();
  const [search, setSearch] = useState("");
  const [minAmount, setMinAmount] = useState<number | null>(null);
  const [maxAmount, setMaxAmount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildParams = useCallback(
    () => ({
      ...rangeParams(range),
      paymentMethod,
      paymentStatus,
      createdBy,
      search: search || undefined,
      minAmount: minAmount ?? undefined,
      maxAmount: maxAmount ?? undefined,
    }),
    [range, paymentMethod, paymentStatus, createdBy, search, minAmount, maxAmount]
  );

  const load = useCallback(
    async (p = page, ps = pageSize) => {
      setLoading(true);
      setError(null);
      try {
        const res = await adminApi.getCollectionsReport({
          ...buildParams(),
          page: p,
          limit: ps,
        });
        setData(res.data.data);
      } catch (e: any) {
        setError(e.response?.data?.message || "Failed to load collection report");
      } finally {
        setLoading(false);
      }
    },
    [buildParams, page, pageSize]
  );

  useEffect(() => {
    load(1, pageSize);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, paymentMethod, paymentStatus, createdBy, minAmount, maxAmount]);

  const summary = data?.summary || {};

  const exportColumns: ExportColumn[] = [
    { title: "Bill #", value: (r) => r.billNumber || "" },
    {
      title: "Date",
      value: (r) => (r.billDate ? dayjs(r.billDate).format("DD/MM/YYYY") : ""),
    },
    {
      title: "Customer",
      value: (r) => r.patient?.name || r.customer?.name || "Walk-in",
    },
    {
      title: "Phone",
      value: (r) => r.patient?.phone || r.customer?.phone || "",
    },
    {
      title: "Payment Method",
      value: (r) => PAYMENT_METHOD_LABELS[r.paymentMethod] || r.paymentMethod || "",
    },
    {
      title: "Status",
      value: (r) => PAYMENT_STATUS_LABELS[r.paymentStatus] || r.paymentStatus || "",
    },
    { title: "Bill Amount", value: (r) => (r.grandTotal || 0).toFixed(2) },
    { title: "Collected", value: (r) => (r.amountPaid || 0).toFixed(2) },
    { title: "Balance", value: (r) => (r.balance || 0).toFixed(2) },
    { title: "Billed By", value: (r) => r.createdBy?.name || "" },
  ];

  const fetchAllRows = async () => {
    const res = await adminApi.getCollectionsReport({
      ...buildParams(),
      format: "all",
    });
    return res.data.data;
  };

  const handleExportCsv = async () => {
    const full = await fetchAllRows();
    const s = full.summary || {};
    exportCsv("Bill Collection Report", exportColumns, full.rows || [], [
      `Period: ${rangeLabel(range)}`,
      `Bills: ${s.billCount || 0} | Billed: ${(s.billed || 0).toFixed(2)} | Collected: ${(s.collected || 0).toFixed(2)} | Balance: ${(s.balance || 0).toFixed(2)}`,
    ]);
  };

  const handleExportPdf = async () => {
    const full = await fetchAllRows();
    const s = full.summary || {};
    exportPdf({
      title: "Bill Collection Report",
      pharmacy,
      subtitle: `Period: ${rangeLabel(range)}`,
      orientation: "landscape",
      columns: exportColumns,
      rows: full.rows || [],
      summary: [
        { label: "Bills", value: String(s.billCount || 0) },
        { label: "Billed", value: pdfCurrency(s.billed) },
        { label: "Collected", value: pdfCurrency(s.collected) },
        { label: "Balance", value: pdfCurrency(s.balance) },
        { label: "Discount", value: pdfCurrency(s.discount) },
      ],
    });
  };

  const reset = () => {
    setRange(DEFAULT_RANGE);
    setPaymentMethod(undefined);
    setPaymentStatus(undefined);
    setCreatedBy(undefined);
    setSearch("");
    setMinAmount(null);
    setMaxAmount(null);
  };

  return (
    <ReportShell
      title="Bill Collection"
      loading={loading}
      error={error}
      onRefresh={() => load()}
      onReset={reset}
      onExportCsv={handleExportCsv}
      onExportPdf={handleExportPdf}
      filters={
        <>
          <RangePicker
            size="small"
            value={range}
            allowClear={false}
            presets={RANGE_PRESETS}
            onChange={(v) => v && setRange(v as DateRange)}
          />
          <Select
            size="small"
            allowClear
            placeholder="Payment method"
            style={{ width: 140 }}
            value={paymentMethod}
            onChange={setPaymentMethod}
            options={Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select
            size="small"
            allowClear
            placeholder="Status"
            style={{ width: 130 }}
            value={paymentStatus}
            onChange={setPaymentStatus}
            options={Object.entries(PAYMENT_STATUS_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select
            size="small"
            allowClear
            placeholder="Billed by"
            style={{ width: 140 }}
            value={createdBy}
            onChange={setCreatedBy}
            options={(data?.byUser || []).map((u: any) => ({
              value: u._id,
              label: u.name,
            }))}
          />
          <InputNumber
            size="small"
            placeholder="Min ₹"
            min={0}
            style={{ width: 90 }}
            value={minAmount}
            onChange={setMinAmount}
          />
          <InputNumber
            size="small"
            placeholder="Max ₹"
            min={0}
            style={{ width: 90 }}
            value={maxAmount}
            onChange={setMaxAmount}
          />
          <Input.Search
            size="small"
            placeholder="Bill # / customer / phone"
            style={{ width: 200 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={() => {
              setPage(1);
              load(1, pageSize);
            }}
            allowClear
          />
        </>
      }
      stats={[
        { title: "Bills", value: summary.billCount || 0 },
        { title: "Billed", value: summary.billed || 0, prefix: "₹", precision: 2 },
        {
          title: "Collected",
          value: summary.collected || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Balance due",
          value: summary.balance || 0,
          prefix: "₹",
          precision: 2,
        },
        { title: "Tax", value: summary.tax || 0, prefix: "₹", precision: 2 },
        {
          title: "Discount",
          value: summary.discount || 0,
          prefix: "₹",
          precision: 2,
        },
      ]}
      extra={
        (data?.byMethod || []).length > 0 ? (
          <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: "8px 12px" } }}>
            <Space wrap split="·">
              {(data.byMethod || []).map((m: any) => (
                <Text key={m._id || "na"} style={{ fontSize: 12 }}>
                  {PAYMENT_METHOD_LABELS[m._id] || m._id || "—"}:{" "}
                  <strong>{formatCurrency(m.collected)}</strong> ({m.count})
                </Text>
              ))}
            </Space>
          </Card>
        ) : null
      }
    >
      <Table
        rowKey="_id"
        size="small"
        sticky
        loading={loading}
        dataSource={data?.rows || []}
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize,
          total: data?.pagination?.total || 0,
          showSizeChanger: true,
          showTotal: (t) => `${t} bills`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
            load(p, ps);
          },
        }}
        columns={[
          { title: "Bill #", dataIndex: "billNumber", width: 120, fixed: "left" },
          {
            title: "Date",
            dataIndex: "billDate",
            width: 100,
            render: (d: string) => (d ? dayjs(d).format("DD/MM/YYYY") : "—"),
          },
          {
            title: "Customer",
            ellipsis: true,
            render: (_: unknown, r: any) =>
              r.patient?.name || r.customer?.name || "Walk-in",
          },
          {
            title: "Method",
            dataIndex: "paymentMethod",
            width: 110,
            render: (m: string) => PAYMENT_METHOD_LABELS[m] || m || "—",
          },
          {
            title: "Status",
            dataIndex: "paymentStatus",
            width: 120,
            render: (s: string) => (
              <Tag color={STATUS_COLORS[s] || "default"}>
                {PAYMENT_STATUS_LABELS[s] || s}
              </Tag>
            ),
          },
          {
            title: "Amount",
            dataIndex: "grandTotal",
            width: 110,
            align: "right" as const,
            render: (v: number) => formatCurrency(v),
          },
          {
            title: "Collected",
            dataIndex: "amountPaid",
            width: 110,
            align: "right" as const,
            render: (v: number) => formatCurrency(v),
          },
          {
            title: "Balance",
            dataIndex: "balance",
            width: 100,
            align: "right" as const,
            render: (v: number) =>
              v > 0 ? <Text type="danger">{formatCurrency(v)}</Text> : formatCurrency(v),
          },
          {
            title: "By",
            width: 110,
            ellipsis: true,
            render: (_: unknown, r: any) => r.createdBy?.name || "—",
          },
        ]}
      />
    </ReportShell>
  );
};

export default CollectionReport;
